import { yupResolver } from '@hookform/resolvers/yup';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { WaypointType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isString } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { TextFormField } from '~components/FormFields/TextFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { SystemRoles } from '~constants/enums';
import {
  nameIdNonRequiredSchema,
  WaypointSchema,
  waypointSchema,
} from '~constants/regexConst';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { useJob } from '~hooks/useJob/useJob';
import useMaterials from '~hooks/useMaterials/useMaterials';
import { WayPoint } from '~hooks/useSites/models';
import { User } from '~hooks/useUsers/models';
import useUsers from '~hooks/useUsers/useUsers';
import { createWaypoint } from '~pages/Sales/Orders/NewOrderForm/utils';
import TrucksV2 from '~pages/Sales/Orders/OrderFormV2/components/TrucksV2';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { AutocompleteAsyncFormField } from '../FormFields/AutocompleteAsyncFormField';
import { SingleDateTimeFormField } from '../FormFields/SingleDateTimeFormField';
import { SiteSelection } from '../Order/SiteSelection/SiteSelection';

interface BulkEditJobDialogProps {
  defaultValues?: BulkEditJobDTO;
  isOpen: boolean;
  jobIds: string[];
  onClose: () => void;
  // Set only when every checked job's order resolves to the same company —
  // the selected jobs may span multiple orders, but any one of those orders'
  // ids is valid for the materials typeahead once the company match holds.
  orderId?: string;
  shouldLockNonEditableFields?: boolean;
}

const bulkEditJobSchema = yup.object().shape({
  notes: yup.string().trim(),
  jobStartAt: yup.string().notRequired(),
  requestedLoadsCount: yup.lazy((val) => {
    return String(val).trim() === ''
      ? yup.string().notRequired()
      : yup
          .number()
          .nullable()
          .min(1, `${t('form_validation_errors.minimum_is_num', { num: 1 })}`)
          .max(25, `${t('form_validation_errors.maximum_is_num', { num: 25 })}`);
  }),
  load: yup.object().notRequired(),
  material: nameIdNonRequiredSchema.notRequired(),
  equipmentTypeGrossCapacity: yup.number().notRequired(),
  foremen: yup
    .array(yup.object().shape({ id: yup.string().required() }).required())
    .notRequired(),
  dropOffWayPoint: waypointSchema.notRequired(),
  pickUpWayPoint: waypointSchema.notRequired(),
  stagingWayPoint: waypointSchema.notRequired(),
  jobUnitOfMeasure: nameIdNonRequiredSchema.notRequired(),
  recurringLoads: yup.boolean().notRequired(),
  equipmentType: nameIdNonRequiredSchema.notRequired(),
  jobQuantity: yup.number().notRequired(),
  equipmentTypeGrossCapacityUnitOfMeasure: nameIdNonRequiredSchema.notRequired(),
});

export type BulkEditJobDTO = yup.InferType<typeof bulkEditJobSchema>;

const BulkEditJobDialog = observer(
  ({
    defaultValues,
    isOpen,
    jobIds,
    onClose,
    orderId,
    shouldLockNonEditableFields,
  }: BulkEditJobDialogProps) => {
    const { bulkEditJob, isUpdating } = useJob();

    const formMethods = useForm<BulkEditJobDTO>({
      resolver: yupResolver(bulkEditJobSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: {
        notes: '',
        jobStartAt: '',
        requestedLoadsCount: null,
        load: null,
        material: null,
        equipmentTypeGrossCapacity: null,
        equipmentTypeGrossCapacityUnitOfMeasure: null,
        foremen: [],
        pickUpWayPoint: null,
        dropOffWayPoint: null,
        stagingWayPoint: null,
        jobUnitOfMeasure: null,
        recurringLoads: null,
        equipmentType: null,
        jobQuantity: null,
        ...defaultValues,
      },
    });

    const {
      clearErrors,
      control,
      formState: { errors, isSubmitted },
      handleSubmit,
      setError,
      setValue,
    } = formMethods;

    const { userStore } = useStores();
    const { getCompanyUsersTypeahead } = useUsers();
    const { getMaterialsByCompanyIdTypeahead } = useMaterials();

    const [jobStartAtIsPartiallyValid, setJobStartAtIsPartiallyValid] = useState(false);

    const companyId = getEffectiveUserCompanyId(userStore);

    useEffect(() => {
      const requestedLoadsCountMessage =
        formMethods.formState.errors?.requestedLoadsCount?.message;

      if (isString(requestedLoadsCountMessage)) {
        formMethods.setError('requestedLoadsCount', {
          type: 'manual',
          message: requestedLoadsCountMessage,
        });
      }
    }, [formMethods.formState.errors]);

    useEffect(() => {
      if (isOpen) {
        setJobStartAtIsPartiallyValid(false);
        formMethods.reset();
        setValue('notes', defaultValues?.notes ?? '');
        setValue('jobStartAt', '');
        setValue('requestedLoadsCount', null);
        setValue('equipmentType', null);
        setValue('material', null);
        setValue('jobUnitOfMeasure', null);
        setValue('recurringLoads', null);
        setValue('foremen', []);
        setValue('pickUpWayPoint', null);
        setValue('dropOffWayPoint', null);
        setValue('stagingWayPoint', null);
        setValue('jobQuantity', null);
        setValue('equipmentTypeGrossCapacity', null);
        setValue('equipmentTypeGrossCapacityUnitOfMeasure', null);
      }
    }, [defaultValues?.notes, isOpen, setValue]);

    const onSubmit = useCallback(
      (event: React.FormEvent) => {
        event.preventDefault();
        event.stopPropagation();

        handleSubmit(async (data: BulkEditJobDTO) => {
          if (jobStartAtIsPartiallyValid) {
            setError('jobStartAt', { message: t('form_validation_errors.invalid_date') });
            return;
          }

          AnalyticsController.track('jobs_bulk_edited', {
            job_count: jobIds.length,
            notes_length: data.notes?.length || 0,
            has_changes: data.notes !== defaultValues?.notes,
          });

          const filteredData = { ...data };
          if (!formMethods.formState.dirtyFields.recurringLoads) {
            filteredData.recurringLoads = null;
          }

          await bulkEditJob(jobIds, filteredData);
          onClose();
        })();
      },
      [
        defaultValues?.notes,
        formMethods.formState.dirtyFields,
        jobIds,
        jobStartAtIsPartiallyValid,
        onClose,
        setError,
      ],
    );

    const handleJobStartAtPartialValidityChange = useCallback(
      (jobStartAtIsPartiallyValid: boolean) => {
        setJobStartAtIsPartiallyValid(jobStartAtIsPartiallyValid);

        if (isSubmitted) {
          if (jobStartAtIsPartiallyValid) {
            setError('jobStartAt', { message: t('form_validation_errors.invalid_date') });
          } else {
            clearErrors('jobStartAt');
          }
        }
      },
      [setError, clearErrors, isSubmitted],
    );

    return (
      <FormProvider {...formMethods}>
        <Dialog isOpen={isOpen} preventCloseOnBackdropClick preventCloseOnEscapeKeyDown>
          <DialogHeader onClose={onClose}>{t('dispatch.order.edit_jobs')}</DialogHeader>

          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography component="span" variant="h6" sx={{ fontWeight: 400 }}>
                  {t('form_fields.details')}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <SingleDateTimeFormField
                  clearOnNull
                  control={control}
                  dateLabel={`${t('form_fields.load_at_date')}`}
                  dense
                  disabled={shouldLockNonEditableFields}
                  errors={errors}
                  isRequired={jobStartAtIsPartiallyValid}
                  name="jobStartAt"
                  onPartialValidityChange={handleJobStartAtPartialValidityChange}
                  timeLabel={`${t('form_fields.load_at_time')}`}
                />

                <FormHelperText>
                  {t('dispatch.dispatch_v2.enter_both_date_and_time')}
                </FormHelperText>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <AutocompleteAsyncFormField
                  control={control}
                  errors={errors}
                  name="material"
                  asyncCallback={getMaterialsByCompanyIdTypeahead}
                  disabled={shouldLockNonEditableFields || !orderId}
                  extraRequestOptions={{ companyId, orderId }}
                  getValue={(item) => item.id}
                  getLabel={(item) => item.name || ''}
                  label={`${t('form_fields.material')}`}
                  clearable={false}
                  inputProps={{ 'data-test-id': 'bulk-edit-material' }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography component="span" variant="h6" sx={{ fontWeight: 400 }}>
                  {t('form_fields.locations')}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <SiteSelection
                  allowSuggestions
                  companyId={companyId}
                  hideAddDefaultSiteAction
                  showMap={false}
                  onPickUpSiteChange={(site) => {
                    let newPickUpWaypoint: WayPoint | null = null;
                    if (site) {
                      newPickUpWaypoint = createWaypoint(
                        site,
                        WaypointType.PICKUP,
                        10,
                        undefined,
                      );
                    }

                    setValue('pickUpWayPoint', newPickUpWaypoint as WaypointSchema, {
                      shouldDirty: true,
                    });
                  }}
                  onDropOffSiteChange={(site) => {
                    let newDropOffWaypoint = null;
                    if (site) {
                      newDropOffWaypoint = createWaypoint(
                        site,
                        WaypointType.DROP_OFF,
                        20,
                        undefined,
                      );
                    }

                    setValue('dropOffWayPoint', newDropOffWaypoint as WaypointSchema, {
                      shouldDirty: true,
                    });
                  }}
                  onStagingSiteChange={(site) => {
                    let newStagingWaypoint: WayPoint | null = null;
                    if (site) {
                      newStagingWaypoint = createWaypoint(
                        site,
                        WaypointType.STAGING,
                        15,
                        undefined,
                      );
                    }

                    setValue('stagingWayPoint', newStagingWaypoint as WaypointSchema, {
                      shouldDirty: true,
                    });
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography component="span" variant="h6" sx={{ fontWeight: 400 }}>
                  {t('form_fields.trucks')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TrucksV2
                  alwaysShowRecurringLoads
                  hideSetCapacityAndUnitsHint
                  hideTruckCount
                  lockNonEditableFields={shouldLockNonEditableFields}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography component="span" variant="h6" sx={{ fontWeight: 400 }}>
                  {t('form_fields.foreman')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <AutocompleteAsyncFormField
                  asyncCallback={getCompanyUsersTypeahead}
                  control={control}
                  disabled={shouldLockNonEditableFields}
                  errors={errors}
                  extraRequestOptions={{ companyId, roleName: SystemRoles.FOREMAN }}
                  filterSelectedOptions={false}
                  getLabel={(user: User) => `${user.firstName} ${user.lastName}`}
                  getValue={(user: User) => user.id}
                  label={`${t('form_fields.assigned_foreman')}`}
                  multiple
                  name="foremen"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  component="span"
                  variant="h6"
                  sx={{ display: 'block', fontWeight: 400 }}
                >
                  {t('dispatch.order.job_notes')}
                </Typography>
                <TextFormField
                  placeholder={`${t('dispatch.order.add_note')}`}
                  control={control}
                  errors={errors}
                  name={'notes'}
                  multiline
                  rows={5}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <SmallButton
              color="secondary"
              disabled={isUpdating}
              onClick={onClose}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>

            <SmallLoadingButton loading={isUpdating} onClick={onSubmit}>
              {t('actions.save')}
            </SmallLoadingButton>
          </DialogActions>
        </Dialog>
      </FormProvider>
    );
  },
);

export default BulkEditJobDialog;
